// Slug-naming-convention audit (item 62 / delivery #71).
//
// Read-only listing of project + repository rows whose slug pre-dates
// the v238 enforcement rule. Each row shows the offending slug + a
// suggested rename so an operator who wants to migrate can do so
// piecemeal via the existing PATCH endpoints — there is no one-click
// auto-rename here because forced renames break bookmarks, CI configs,
// and API-token scopes.

import { Link } from 'react-router-dom';
import { ShieldAlert, FolderTree, Box, ArrowRight } from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { useSlugAudit } from '../../api/slugAudit';

export function AdminSlugAudit() {
  const { data, error, isLoading } = useSlugAudit();
  const projectCount = data?.projects.length ?? 0;
  const repoCount = data?.repositories.length ?? 0;
  const total = projectCount + repoCount;

  return (
    <AdminShell
      title="Slug naming audit"
      description="Existing slugs that don't match the v238 naming convention. Renames are operator-driven — listed here for review, not auto-applied."
    >
      <Card>
        <CardHeader
          title="Naming convention"
          description="Lowercase letters/digits, dots as separators (e.g. acme.platform). No hyphens, underscores, leading digits, or special characters. Length 2..63."
          actions={
            isLoading ? (
              <Spinner size="xs" />
            ) : (
              <Badge tone={total === 0 ? 'success' : 'warning'}>
                {total === 0 ? 'All compliant' : `${total} non-compliant`}
              </Badge>
            )
          }
        />
        <CardBody>
          <p className="text-sm text-fg-muted">
            New project + repository slugs created via the API or the create-form
            here are validated against the rule above. Existing rows are
            grandfathered so URL bookmarks, CI configs, and API-token scopes
            stay valid. To migrate a legacy slug, PATCH the row with a new slug
            that matches the rule (the row's UUID stays stable; only the
            URL-segment changes).
          </p>
        </CardBody>
      </Card>

      {error && (
        <Card className="border-danger/30 bg-danger/5">
          <CardBody>
            <p className="text-danger font-mono text-sm">{String(error)}</p>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <FolderTree className="h-4 w-4 text-fg-muted" />
              Legacy-named projects
              <Badge tone={projectCount === 0 ? 'success' : 'warning'}>{projectCount}</Badge>
            </span>
          }
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted">Current slug</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Name</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Suggested rename</th>
              </tr>
            </thead>
            <tbody>
              {data?.projects.length ? (
                data.projects.map((p) => (
                  <tr key={p.id} className="border-b border-border/60 last:border-0">
                    <td className="px-5 py-3 font-mono text-warning">{p.slug}</td>
                    <td className="px-5 py-3 text-fg">{p.name}</td>
                    <td className="px-5 py-3 font-mono text-fg-muted">
                      {p.suggestion ? (
                        <span className="inline-flex items-center gap-2">
                          <ArrowRight className="h-3.5 w-3.5" />
                          <span className="text-success">{p.suggestion}</span>
                        </span>
                      ) : (
                        <span className="text-fg-subtle italic">—</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={3} className="px-5 py-8 text-center text-fg-muted text-sm">
                    {isLoading ? (
                      <span className="inline-flex items-center gap-2">
                        <Spinner size="xs" />
                        Scanning projects…
                      </span>
                    ) : (
                      'No legacy-named projects.'
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <CardHeader
          title={
            <span className="flex items-center gap-2">
              <Box className="h-4 w-4 text-fg-muted" />
              Legacy-named repositories
              <Badge tone={repoCount === 0 ? 'success' : 'warning'}>{repoCount}</Badge>
            </span>
          }
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted">Project</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Current slug</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Name</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Suggested rename</th>
              </tr>
            </thead>
            <tbody>
              {data?.repositories.length ? (
                data.repositories.map((r) => (
                  <tr key={r.id} className="border-b border-border/60 last:border-0">
                    <td className="px-5 py-3">
                      <Link
                        to={`/projects/${r.project_id}`}
                        className="font-mono text-accent hover:underline"
                      >
                        {r.project_slug}
                      </Link>
                    </td>
                    <td className="px-5 py-3 font-mono text-warning">{r.slug}</td>
                    <td className="px-5 py-3 text-fg">{r.name}</td>
                    <td className="px-5 py-3 font-mono text-fg-muted">
                      {r.suggestion ? (
                        <span className="inline-flex items-center gap-2">
                          <ArrowRight className="h-3.5 w-3.5" />
                          <span className="text-success">{r.suggestion}</span>
                        </span>
                      ) : (
                        <span className="text-fg-subtle italic">—</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-5 py-8 text-center text-fg-muted text-sm">
                    {isLoading ? (
                      <span className="inline-flex items-center gap-2">
                        <Spinner size="xs" />
                        Scanning repositories…
                      </span>
                    ) : (
                      'No legacy-named repositories.'
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="border-border/50">
        <CardBody>
          <div className="flex items-start gap-3">
            <ShieldAlert className="h-5 w-5 text-fg-muted shrink-0 mt-0.5" />
            <div className="text-sm text-fg-muted space-y-1">
              <p className="text-fg font-medium">How to migrate</p>
              <p>
                Edit the project / repository in question and set the slug to the
                suggested value (or any other slug that matches the rule). The
                row's UUID stays stable so existing role-bindings, retention
                policies, security blocks, and webhook subscriptions are
                preserved automatically. Update bookmarks, CI configs, and any
                hard-coded clients afterwards.
              </p>
            </div>
          </div>
        </CardBody>
      </Card>
    </AdminShell>
  );
}
